import { OverlayTriggerHandle } from "rsuite/esm/internals/Overlay/OverlayTrigger";
import { ColorPicker } from "@mantine/core";
import { Popover, Whisper } from "rsuite";
import { useEffect, useRef, useState } from "react";

import { Field } from "../../../types/TenantInterfaces";
import DisplayRecord from "../DisplayRecord";
import EmptyField from "../EmptyField";
import { Button } from "reactstrap";

interface Props {
  tableView: boolean;
  field: Field;
  existingValue: string;
  mode: "r" | "rw";
  modifyTrigger: "click" | "doubleclick";
  recordID: string;
  error: any;
  onUpdate: (data: {
    field: Field;
    recordID: string;
    payload: Record<string, string>;
    onSuccess?: () => void;
    onError?: (error: any) => void;
  }) => void;
}

const ColorRecord = ({
  tableView,
  field,
  existingValue,
  mode,
  modifyTrigger,
  recordID,
  error,
  onUpdate,
}: Props) => {
  const [colorData, setColorData] = useState<{
    color: string;
    valid: boolean;
    open: boolean;
    error: string | null;
  }>({
    color: existingValue,
    valid: true,
    open: false,
    error: null,
  });
  const whisperRef = useRef<OverlayTriggerHandle | null>(null);

  // Sync state with existing value
  useEffect(() => {
    setColorData((prev) => ({
      ...prev,
      color: existingValue,
    }));
  }, [existingValue]);

  return (
    <Whisper
      ref={whisperRef}
      trigger="click"
      placement="auto"
      speaker={
        <Popover className="tw:p-0">
          <div className="tw:pt-4 tw:px-4 tw:pb-2" style={{ width: 200 }}>
            <ColorPicker
              value={colorData.color}
              fullWidth
              format="hexa"
              onChange={(color) => {
                setColorData((prev) => ({
                  ...prev,
                  color: color,
                  error: null,
                }));
              }}
            />
            <div className="tw:relative">
              <input
                value={colorData.color}
                className="tw:w-full tw:border tw:rounded-sm tw:mt-2 tw:px-2 tw:py-1 tw:text-sm"
                onChange={(e) => {
                  setColorData((prev) => ({
                    ...prev,
                    color: e.target.value,
                    error: null,
                  }));
                }}
                type="text"
              />
              <i
                onClick={() => {
                  setColorData((prev) => ({ ...prev, color: "" }));
                }}
                className="mdi mdi-close cursor-pointer tw:text-sm tw:absolute tw:right-1 tw:top-[13px]"
              ></i>
            </div>
            {(!!colorData.error || !!error) && (
              <p className="tw:bg-danger tw:rounded tw:p-1 tw:text-white tw:text-xs tw:mt-2 tw:mb-0">
                {colorData.error || "Something went wrong"}
              </p>
            )}
            <div className="tw:w-full tw:flex tw:justify-end tw:m-0">
              <Button
                onClick={handleSave}
                size="sm"
                className="tw:mt-2"
                color="primary"
              >
                Save
              </Button>
            </div>
          </div>
        </Popover>
      }
    >
      <div>
        <DisplayRecord
          mode={mode}
          tableView={tableView}
          modifyTrigger={modifyTrigger}
          isSensitive={field?.Sensitive ?? false}
          displayComponent={(_: any, innerRef: any) => (
            <div
              ref={innerRef}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
            >
              {!existingValue ? (
                <EmptyField table={tableView} />
              ) : (
                <div className="tw:flex tw:flex-row tw:items-center">
                  <span
                    className="tw:m-0 tw:p-0"
                    style={{
                      backgroundColor: existingValue,
                      display: "inline-block",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "1px solid #e5e7eb",
                      zIndex: 1,
                    }}
                  />
                  <p className="tw:p-0 tw:m-0 tw:ml-2 tw:text-xs">
                    {existingValue}
                  </p>
                </div>
              )}
            </div>
          )}
          openModify={() => {
            setColorData((prev) => ({ ...prev, open: !prev.open }));
            whisperRef.current?.open();
          }}
        />
      </div>
    </Whisper>
  );

  function handleSave() {
    if (colorData.color === existingValue) return;
    if (!validateColor(colorData.color) && colorData.color !== "") {
      setColorData((prev) => ({
        ...prev,
        valid: false,
        error: "Invalid hexa value",
      }));
      return;
    }
    onUpdate({
      field: field,
      recordID: recordID,
      payload: { [field.APIName]: colorData.color },
      onSuccess: () => {
        whisperRef.current?.close();
      },
    });
  }

  function validateColor(color: string) {
    // Check if color string is a valid hex color
    const hexColorRegex = /^#?([a-fA-F0-9]{3}|[a-fA-F0-9]{6}|[a-fA-F0-9]{8})$/;
    return hexColorRegex.test(color);
  }
};

export default ColorRecord;
